/**
 * Transport for the Serverpod control plane.
 *
 * Mirrors the client's cloudClient.ts: `POST /{endpoint}/{method}` with a JSON
 * object of scalars, and a `{ok, data | code, error}` envelope inside a JSON
 * string. See backend/docs/API.md for the contract.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string
  ) {
    super(message)
  }

  get isAuth(): boolean {
    return this.code === 'unauthorized'
  }
}

/**
 * In development Vite proxies `/_api` to the Serverpod port, avoiding CORS; in
 * production VITE_API_BASE points at the deployed API origin.
 */
const BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/+$/, '') ?? '/_api'

const SESSION_KEY = 'yts.session'

export function storedSession(): string | null {
  return localStorage.getItem(SESSION_KEY)
}

export function storeSession(token: string | null): void {
  if (token) localStorage.setItem(SESSION_KEY, token)
  else localStorage.removeItem(SESSION_KEY)
}

export async function call<T = Record<string, unknown>>(
  endpoint: string,
  method: string,
  params: Record<string, unknown> = {}
): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE}/${endpoint}/${method}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    })
  } catch (err) {
    throw new ApiError(
      err instanceof Error ? err.message : 'the server is unreachable',
      'offline'
    )
  }

  const text = await response.text()
  if (!response.ok) {
    throw new ApiError(`server returned HTTP ${response.status}`, 'internal')
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(text)
    // Endpoints return a String, so the body is that string encoded as JSON.
    // The second decode is conditional, so a Serverpod that returns the object
    // directly keeps working.
    if (typeof parsed === 'string') parsed = JSON.parse(parsed)
  } catch {
    throw new ApiError('the server returned a malformed response', 'internal')
  }

  const envelope = parsed as { ok?: boolean; data?: T; code?: string; error?: string }
  if (typeof envelope?.ok !== 'boolean') {
    throw new ApiError('the server returned an unexpected response', 'internal')
  }
  if (!envelope.ok) {
    throw new ApiError(envelope.error ?? 'request failed', envelope.code ?? 'internal')
  }
  return (envelope.data ?? {}) as T
}

/** Authenticated call: injects the stored session token. */
export async function auth<T = Record<string, unknown>>(
  method: string,
  params: Record<string, unknown> = {}
): Promise<T> {
  const sessionToken = storedSession()
  if (!sessionToken) throw new ApiError('not signed in', 'unauthorized')
  try {
    return await call<T>('admin', method, { sessionToken, ...params })
  } catch (err) {
    // An expired session must not leave a dead token behind, or every later call
    // fails the same way with no path back to the login screen.
    if (err instanceof ApiError && err.isAuth) storeSession(null)
    throw err
  }
}

// ---- shapes returned by the admin endpoint ----

/**
 * How far past its date a licence is, and therefore what has been taken away.
 * The thresholds live in the server's business/entitlement.dart — the console
 * renders the rung it is told rather than recomputing it from a date, so the
 * two can never disagree about whether a site is still being served.
 */
export type EntitlementRung =
  | 'full'
  | 'warned'
  | 'featuresWithheld'
  | 'retentionCut'
  | 'silent'

export interface Entitlement {
  tier: string
  rung: EntitlementRung
  /** Already has the ladder applied — withheld features are absent, not flagged. */
  features: string[]
  /** 0 means licensing imposes no limit; the device's own setting decides. */
  retentionDays: number
  expiresAt: string | null
  /** Nothing has been sold to this account, so nothing is withheld. */
  unmetered: boolean
  /** False only on the last rung, where the device is handed no config. */
  mayReceiveWork: boolean
  deviceQuota: number | null
}

export interface DeviceSummary {
  id: number
  name: string
  platform: string
  appVersion: string
  createdAt: string
  lastSeenAt: string | null
  online: boolean
  revoked: boolean
  /** False while the device has self-registered but no operator has accepted it. */
  approved: boolean
  streamState: string
  lastErrorText: string | null
  stats: { fps?: number; bitrateKbps?: number; speed?: number; droppedFrames?: number } | null
  desiredConfigVersion: number
  reportedConfigVersion: number
  configPending: boolean
  entitlement: Entitlement
  /** Null is unassigned, which only staff can see. */
  customerId: number | null
}

export interface DeviceDetail extends DeviceSummary {
  desiredConfig: Record<string, unknown> | null
  reportedConfig: Record<string, unknown> | null
  /** Null means the licence never lapses — the state of every pre-licensing device. */
  licenseUntil: string | null
  licenseNote: string | null
  /** Decided by the server's clock, the same one the heartbeat gate uses. */
  licenseExpired: boolean
}

export interface ConfigRevisionRow {
  version: number
  createdAt: string
  authorEmail: string
  note: string | null
}

export interface CommandRow {
  id: number
  action: string
  createdAt: string
  deliveredAt: string | null
  ackedAt: string | null
  ok: boolean | null
  resultText: string | null
  issuedByEmail: string
}

export interface EventRow {
  id: number
  ts: string
  level: string
  kind: string
  text: string
}

export interface EnrollTokenRow {
  id: number
  code: string
  note: string | null
  createdAt: string
  expiresAt: string
  usedByDeviceId: number | null
  usedAt: string | null
  expired: boolean
}

export interface AdminRow {
  id: number
  email: string
  createdAt: string
  lastLoginAt: string | null
  disabled: boolean
  isYou: boolean
}

export interface InviteRow {
  id: number
  code: string
  email: string | null
  note: string | null
  createdAt: string
  createdByEmail: string
  expiresAt: string
  usedAt: string | null
  revoked: boolean
  expired: boolean
}

export interface PlanRow {
  id: number
  code: string
  name: string
  /** Satang. Money that has been through a float is money somebody argues about. */
  priceMonthlySatang: number
  retentionDays: number
  features: string[]
  active: boolean
}

export interface SubscriptionRow {
  id: number
  customerId: number
  planId: number
  planCode: string | null
  planName: string | null
  startsAt: string
  endsAt: string
  /** Null means unmetered. Over-quota is reported, never enforced. */
  deviceQuota: number | null
  status: 'trial' | 'active' | 'past_due' | 'suspended'
  poNote: string | null
  rung: EntitlementRung
}

export interface CustomerRow {
  id: number
  name: string
  note: string | null
  active: boolean
  createdAt: string
  deviceCount: number
  subscription: SubscriptionRow | null
  overQuota: boolean
}

export interface BillingSummary {
  customer: { id: number; name: string; active: boolean }
  subscription: SubscriptionRow | null
  deviceCount: number
  overQuota: boolean
}

export interface SignupInfo {
  /** No accounts exist yet, so the first person can sign up without an invite. */
  firstRun: boolean
  openSignup: boolean
  inviteRequired: boolean
}

export const api = {
  signupInfo: () => call<SignupInfo>('admin', 'signupInfo'),
  signup: (email: string, password: string, inviteCode: string) =>
    call<{ sessionToken: string; email: string; expiresAt: string }>('admin', 'signup', {
      email,
      password,
      inviteCode
    }),
  checkInvite: (inviteCode: string) =>
    call<{ valid: boolean; email?: string | null; reason?: string }>(
      'admin',
      'checkInvite',
      { inviteCode }
    ),
  login: (email: string, password: string) =>
    call<{ sessionToken: string; email: string; expiresAt: string }>('admin', 'login', {
      email,
      password
    }),
  logout: (sessionToken: string) => call('admin', 'logout', { sessionToken }),

  listAdmins: () => auth<{ admins: AdminRow[] }>('listAdmins'),
  setAdminDisabled: (adminUserId: number, disabled: boolean) =>
    auth<{ disabled: boolean; email: string }>('setAdminDisabled', {
      adminUserId,
      disabled
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    auth<{ changed: boolean }>('changePassword', { currentPassword, newPassword }),

  listInvites: () => auth<{ invites: InviteRow[] }>('listInvites'),
  createInvite: (email: string, note: string) =>
    auth<{ code: string; email: string | null; expiresAt: string; expiresInHours: number }>(
      'createInvite',
      { email, note }
    ),
  revokeInvite: (inviteId: number) => auth<{ revoked: boolean }>('revokeInvite', { inviteId }),
  setOpenSignup: (enabled: boolean) =>
    auth<{ openSignup: boolean }>('setOpenSignup', { enabled }),

  listDevices: () => auth<{ devices: DeviceSummary[] }>('listDevices'),
  getDevice: (deviceId: number) =>
    auth<{ device: DeviceDetail; revisions: ConfigRevisionRow[]; commands: CommandRow[] }>(
      'getDevice',
      { deviceId }
    ),
  setDesiredConfig: (deviceId: number, configJson: string, note: string) =>
    auth<{ version: number }>('setDesiredConfig', { deviceId, configJson, note }),
  rollbackConfig: (deviceId: number, version: number) =>
    auth<{ version: number }>('rollbackConfig', { deviceId, version }),
  sendCommand: (deviceId: number, action: string, argsJson = '') =>
    auth<{ commandId: number; action: string }>('sendCommand', {
      deviceId,
      action,
      argsJson
    }),
  listEvents: (deviceId: number, limit = 100) =>
    auth<{ events: EventRow[] }>('listEvents', { deviceId, limit }),
  createEnrollToken: (note: string) =>
    auth<{ code: string; expiresAt: string; expiresInMinutes: number }>(
      'createEnrollToken',
      { note }
    ),
  listEnrollTokens: () => auth<{ tokens: EnrollTokenRow[] }>('listEnrollTokens'),
  renameDevice: (deviceId: number, name: string) =>
    auth<{ renamed: boolean; name: string }>('renameDevice', { deviceId, name }),
  setDeviceApproved: (deviceId: number, approved: boolean) =>
    auth<{ approved: boolean }>('setDeviceApproved', { deviceId, approved }),
  /** Empty `until` clears the expiry, which means the licence never lapses. */
  setDeviceLicense: (deviceId: number, until: string, note: string) =>
    auth<{ licenseUntil: string | null; licenseNote: string | null }>('setDeviceLicense', {
      deviceId,
      until,
      note
    }),
  setDeviceRevoked: (deviceId: number, revoked: boolean) =>
    auth<{ revoked: boolean }>('setDeviceRevoked', { deviceId, revoked }),

  // ---- accounts, plans and what they have been sold ----

  listCustomers: () =>
    auth<{ customers: CustomerRow[]; unassigned: number }>('listCustomers'),
  createCustomer: (name: string, note: string) =>
    auth<{ id: number; name: string }>('createCustomer', { name, note }),
  /** 0 moves the device back to unassigned, which only staff can see. */
  setDeviceCustomer: (deviceId: number, customerId: number) =>
    auth<{ customerId: number | null; customer: string }>('setDeviceCustomer', {
      deviceId,
      customerId
    }),

  listPlans: () => auth<{ plans: PlanRow[] }>('listPlans'),
  createPlan: (
    code: string,
    name: string,
    priceMonthlySatang: number,
    retentionDays: number,
    features: string[]
  ) =>
    auth<PlanRow>('createPlan', {
      code,
      name,
      priceMonthlySatang,
      retentionDays,
      featuresJson: JSON.stringify(features)
    }),
  setPlanActive: (planId: number, active: boolean) =>
    auth<PlanRow>('setPlanActive', { planId, active }),

  /**
   * Sells or renews. Writes a new subscription and supersedes the old one, so
   * last year's terms survive this year's sale.
   */
  setSubscription: (
    customerId: number,
    planId: number,
    endsAt: string,
    deviceQuota: number,
    poNote: string
  ) =>
    auth<SubscriptionRow>('setSubscription', {
      customerId,
      planId,
      endsAt,
      deviceQuota,
      poNote
    }),
  setSubscriptionStatus: (customerId: number, status: SubscriptionRow['status']) =>
    auth<{ status: string }>('setSubscriptionStatus', { customerId, status }),
  /** Readable by the customer's own operators, so nobody has to phone in to ask. */
  billingSummary: (customerId: number) =>
    auth<BillingSummary>('billingSummary', { customerId }),

  /** Fleet-watchdog alert channels: the server pages someone when a box goes silent. */
  getAlertSettings: () => auth<AlertSettings>('getAlertSettings'),
  setAlertSettings: (s: {
    telegramToken: string
    telegramChat: string
    webhookUrl: string
    offlineAfterSec: number
  }) => auth<{ saved: boolean; offlineAfterSec: number }>('setAlertSettings', s),
  testAlert: () =>
    auth<{ results: { channel: string; ok: boolean; error: string | null }[] }>('testAlert')
}

export interface AlertSettings {
  telegramConfigured: boolean
  telegramTokenTail: string
  telegramChat: string
  webhookUrl: string
  offlineAfterSec: number
}
